import { countryName, type BookPreview, type Plan, type TableRows } from '@trippath/shared';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { dayShort } from '../../lib/format';

/** A choice ticked or not, with what it means under it. */
export function Check({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const hintId = useId();
  return (
    <div className="export-check">
      <label>
        <input
          type="checkbox"
          checked={checked}
          aria-describedby={hint === undefined ? undefined : hintId}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
        />
        {label}
      </label>
      {hint !== undefined && <small id={hintId}>{hint}</small>}
    </div>
  );
}

/** One of a few, side by side: the one chosen pressed. */
export function Choose<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly (readonly [T, string])[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map(([option, said]) => (
        <button
          key={option}
          type="button"
          aria-pressed={value === option}
          onClick={() => {
            onChange(option);
          }}
        >
          {said}
        </button>
      ))}
    </div>
  );
}

/** A group of choices under its heading. */
export function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="export-group" role="group" aria-label={title}>
      <span className="export-caps">{title}</span>
      {children}
    </div>
  );
}

/** A file's size as read: `2.8 MB`, `340 kB`. */
export function sizeSaid(bytes: number, language: string): string {
  const format = (value: number, digits: number) =>
    new Intl.NumberFormat(language === 'et' ? 'et-EE' : 'en-GB', {
      maximumFractionDigits: digits,
    }).format(value);
  return bytes >= 1_000_000
    ? `${format(bytes / 1_000_000, 1)} MB`
    : `${format(Math.max(1, Math.round(bytes / 1000)), 0)} kB`;
}

/** The route drawn small, its stops in a box 100 by 54. */
function RouteSketch({ plan }: { plan: Plan }) {
  const points = plan.stops.flatMap((stop) => (stop.place ? [stop.place] : []));
  const lons = points.map((p) => p.lon);
  const lats = points.map((p) => p.lat);
  const [west, east] = [Math.min(...lons), Math.max(...lons)];
  const [south, north] = [Math.min(...lats), Math.max(...lats)];
  const scale = Math.min(88 / Math.max(east - west, 0.01), 42 / Math.max(north - south, 0.01));
  const line = points
    .map(
      (p) =>
        `${(6 + (p.lon - west) * scale).toFixed(1)},${(6 + (north - p.lat) * scale).toFixed(1)}`,
    )
    .join(' ');
  return (
    <svg viewBox="0 0 100 54" aria-hidden="true">
      <polyline points={line} />
    </svg>
  );
}

/** The book's first pages as they come out: the cover, a day, a booking, a country. */
export function BookPages({ plan, preview }: { plan: Plan; preview: BookPreview | null }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const first = plan.stops.at(0);
  const booking = plan.stops.flatMap((stop) => stop.bookings).at(0);
  const country = plan.stops.find((stop) => stop.country !== '')?.country;
  const starts = preview?.starts;
  const sheets: {
    key: string;
    page: number | null | undefined;
    caption: string;
    body: ReactNode;
  }[] = [
    {
      key: 'cover',
      page: starts?.cover,
      caption: 'exportTrip.coverPage',
      body: (
        <>
          <h4>{plan.trip.name}</h4>
          <span className="export-sheet-line">
            {dayShort(plan.trip.start, language)} – {dayShort(plan.trip.end, language)}
          </span>
          <div className="export-sheet-map">
            <RouteSketch plan={plan} />
          </div>
        </>
      ),
    },
    {
      key: 'day',
      page: starts?.days,
      caption: 'exportTrip.dayPage',
      body: (
        <h4>{first ? `${dayShort(first.start, language)} · ${first.city}` : plan.trip.name}</h4>
      ),
    },
    {
      key: 'booking',
      page: starts?.bookings,
      caption: 'exportTrip.bookingPage',
      body: <h4>{booking?.name}</h4>,
    },
    {
      key: 'entry',
      page: starts?.entry,
      caption: 'exportTrip.entryPage',
      body: (
        <h4>
          {t('exportTrip.entryOf', {
            country: country === undefined ? '' : countryName(country, language),
          })}
        </h4>
      ),
    },
  ];
  return (
    <div className="export-pages" aria-label={t('exportTrip.preview')}>
      {sheets
        .filter((sheet) => typeof sheet.page === 'number')
        .map((sheet) => (
          <figure key={sheet.key}>
            <div className="export-sheet">
              {sheet.body}
              <span className="export-sheet-rule" />
              <span className="export-sheet-rule export-sheet-rule-short" />
            </div>
            <figcaption>{t(sheet.caption, { page: sheet.page })}</figcaption>
          </figure>
        ))}
    </div>
  );
}

/** The first rows of the first table chosen. */
export function RowsPreview({ tables }: { tables: TableRows[] | null }) {
  const { t } = useTranslation();
  const first = tables?.at(0);
  if (!first || first.rows.length < 2) {
    return <p className="quiet">{t('exportTrip.noRows')}</p>;
  }
  const [head, ...body] = first.rows;
  return (
    <div className="table-scroll">
      <table className="export-table">
        <caption>{first.name}</caption>
        <thead>
          <tr>
            {head?.map((cell) => (
              <th key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, at) => (
            // The rows have no id of their own: they are the file's first rows, in order.
            <tr key={at}>
              {row.map((cell, column) => (
                <td key={column}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The calendar's link, or that there is none yet. */
export function FeedField({ link }: { link: string | null }) {
  const { t } = useTranslation();
  return (
    <div className="field">
      <span className="field-label">{t('exportTrip.feed')}</span>
      <output className="export-feed">{link ?? t('exportTrip.feedNone')}</output>
    </div>
  );
}
